const INFO_ICON_SVG =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
  '<circle cx="8" cy="5" r="0.9" fill="currentColor"/>' +
  '<rect x="7.3" y="7" width="1.4" height="5" rx="0.7" fill="currentColor"/>' +
  '</svg>';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const SPEND_LABEL_GAP = 12;
// Width of the component (its container) below which level amounts and the
// "Level" suffix are hidden, regardless of the viewport width.
const COMPACT_WIDTH = 680;

let tooltipIdCounter = 0;

export class CardNextLevel {
  /**
   * Creates a new instance of a CardNextLevel component.
   * @param {HTMLElement} element CardNextLevel DOM element
   */
  constructor(element) {
    this.el = element;
    this._refs = {
      levels: this.el.querySelector('[data-progress-bar-levels]'),
      track: this.el.querySelector('[data-progress-bar-track]'),
      currentFill: this.el.querySelector('[data-progress-bar-current]'),
      estimatedFill: this.el.querySelector('[data-progress-bar-estimated]'),
      currentKnob: this.el.querySelector('[data-progress-bar-knob-current]'),
      estimatedKnob: this.el.querySelector('[data-progress-bar-knob-estimated]'),
      spend: this.el.querySelector('[data-progress-bar-spend]'),
      progressBar: this.el.querySelector('[data-progress-bar]'),
    };
    this._props = {
      levelEntries: [],
      numberFormatter: undefined,
      currentSpendItem: undefined,
      estimatedSpendItem: undefined,
      currentValueEl: undefined,
      estimatedValueEl: undefined,
      estimatedTooltipTextEl: undefined,
      currentPercent: 0,
      estimatedPercent: 0,
      showEstimated: false,
      resizeRaf: null,
    };

    this.config = this.parseConfiguration();
    if (!this.config) return;

    if (!this.hasRequiredRefs()) return;

    this.onResize = this.onResize.bind(this);
    this.init();
  }

  parseConfiguration() {
    try {
      return JSON.parse(this.el.dataset.cardNextLevelConfig);
    } catch (e) {
      this.el.dataset.cardNextLevelError = 'true';
      console.error('CardNextLevel: configuration to parse is not valid JSON!', e);
    }
  }

  hasRequiredRefs() {
    const { levels, track, spend } = this._refs;

    if (!levels || !track || !spend) {
      this.el.dataset.cardNextLevelError = 'true';
      console.error('CardNextLevel: required DOM references are missing.');
      return false;
    }

    return true;
  }

  init() {
    const { statusLevels, cultureInfoName } = this.config;

    this._props.levelEntries = Object.entries(statusLevels).sort((a, b) => a[1] - b[1]);
    this._props.numberFormatter = new Intl.NumberFormat(cultureInfoName, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    this.renderLevels();
    this.renderSpend();
    this.render();
    this.updateWideClass();

    window.addEventListener('resize', this.onResize);
  }

  onResize() {
    if (this._props.resizeRaf) return;

    this._props.resizeRaf = window.requestAnimationFrame(() => {
      this._props.resizeRaf = null;
      this.positionSpend();
      this.updateWideClass();
    });
  }

  updateWideClass() {
    const { progressBar } = this._refs;
    if (!progressBar) return;
    const isWide = progressBar.offsetWidth >= COMPACT_WIDTH;
    progressBar.classList.toggle('progress-bar--wide', isWide);
    progressBar.classList.toggle('progress-bar--narrow', !isWide);
  }

  toPercent(value) {
    const { levelEntries } = this._props;
    const n = levelEntries.length;
    if (n < 2) return 0;

    const firstThreshold = levelEntries[0][1];
    const lastThreshold = levelEntries[n - 1][1];

    if (value <= firstThreshold) return 0;
    if (value >= lastThreshold) return 100;

    // Equal spacing: interpolate within the matching segment.
    for (let i = 0; i < n - 1; i += 1) {
      const low = levelEntries[i][1];
      const high = levelEntries[i + 1][1];

      if (value >= low && value <= high) {
        const segmentFraction = high === low ? 0 : (value - low) / (high - low);
        return ((i + segmentFraction) / (n - 1)) * 100;
      }
    }

    return 100;
  }

  getLevelKeyForValue(value) {
    const { levelEntries } = this._props;
    let activeKey = levelEntries[0][0];

    levelEntries.forEach(([key, threshold]) => {
      if (value >= threshold) {
        activeKey = key;
      }
    });

    return activeKey;
  }

  formatCurrency(value) {
    const { currencySymbol } = this.config;
    return `${currencySymbol}${this._props.numberFormatter.format(value)}`;
  }

  renderLevels() {
    const { translations, shortNames } = this.config;
    const { levelEntries } = this._props;

    this._refs.levels.innerHTML = '';

    levelEntries.forEach(([key, threshold], index) => {
      const percent = levelEntries.length > 1 ? (index / (levelEntries.length - 1)) * 100 : 0;
      const item = document.createElement('div');
      item.className = 'progress-bar__level';
      item.dataset.levelKey = key;
      item.style.left = `${percent}%`;

      if (index === 0) {
        item.classList.add('progress-bar__level--first');
      }
      if (index === levelEntries.length - 1) {
        item.classList.add('progress-bar__level--last');
      }

      const name = document.createElement('span');
      name.className = 'progress-bar__level-name';

      const fullNameEl = document.createElement('span');
      fullNameEl.className = 'progress-bar__level-name--full';
      fullNameEl.textContent = translations[key];

      const shortNameEl = document.createElement('span');
      shortNameEl.className = 'progress-bar__level-name--short';
      shortNameEl.textContent = shortNames[key];

      name.appendChild(fullNameEl);
      name.appendChild(shortNameEl);

      const value = document.createElement('span');
      value.className = 'progress-bar__level-value';
      value.textContent = this.formatCurrency(threshold);

      const connector = document.createElement('span');
      connector.className = 'progress-bar__level-connector';

      item.appendChild(name);
      item.appendChild(value);
      item.appendChild(connector);
      this._refs.levels.appendChild(item);
    });
  }

  renderSpend() {
    const { currentSpendLabel, estimatedSpendLabel, tooltip } = this.config;

    this._refs.spend.innerHTML = '';

    const current = this.createSpendItem({
      modifier: 'progress-bar__spend-item--current',
      label: currentSpendLabel,
    });
    const estimated = this.createSpendItem({
      modifier: 'progress-bar__spend-item--estimated',
      label: estimatedSpendLabel,
      tooltip,
    });

    this._props.currentSpendItem = current.item;
    this._props.currentValueEl = current.valueEl;
    this._props.estimatedSpendItem = estimated.item;
    this._props.estimatedValueEl = estimated.valueEl;
    this._props.estimatedTooltipTextEl = estimated.tooltipTextEl;

    this._refs.spend.appendChild(current.item);
    this._refs.spend.appendChild(estimated.item);
  }

  createSpendItem({ modifier, label, tooltip }) {
    const item = document.createElement('div');
    item.className = `progress-bar__spend-item ${modifier}`;

    const labelEl = document.createElement('span');
    labelEl.className = 'progress-bar__spend-label';
    labelEl.appendChild(document.createTextNode(label));

    let tooltipTextEl;
    if (tooltip) {
      const builtTooltip = this.createTooltip(tooltip);
      tooltipTextEl = builtTooltip.textEl;
      labelEl.appendChild(builtTooltip.wrapper);
    }

    const valueEl = document.createElement('span');
    valueEl.className = 'progress-bar__spend-value';

    item.appendChild(labelEl);
    item.appendChild(valueEl);

    return { item, valueEl, tooltipTextEl };
  }

  createTooltip(text) {
    tooltipIdCounter += 1;
    const contentId = `card-next-level-tooltip-${tooltipIdCounter}`;

    const wrapper = document.createElement('span');
    wrapper.className = 'progress-bar__tooltip';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'progress-bar__tooltip-button';
    button.setAttribute('aria-label', 'More information');
    button.setAttribute('aria-controls', contentId);
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = INFO_ICON_SVG;

    const content = document.createElement('span');
    content.className = 'progress-bar__tooltip-content is-hidden';
    content.id = contentId;
    content.setAttribute('role', 'tooltip');

    const arrow = document.createElement('span');
    arrow.className = 'progress-bar__tooltip-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="17" viewBox="0 0 22 17" fill="none">' +
      '<path d="M7.67564 1.48553C9.27663 -0.495175 12.2964 -0.495175 13.8974 1.48553L20.6764 9.87237C22.7907 12.4881 20.9289 16.3868 17.5655 16.3868H4.00749C0.64409 16.3868 -1.21765 12.4881 0.896641 9.87237L7.67564 1.48553Z" fill="#FEFEFE"/>' +
      '</svg>';

    const contentText = document.createElement('span');
    contentText.className = 'progress-bar__tooltip-text';
    contentText.textContent = text;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'progress-bar__tooltip-close';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.innerHTML =
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
      '<line x1="2" y1="2" x2="14" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<line x1="14" y1="2" x2="2" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>';

    const closeOnOutside = (event) => {
      if (!wrapper.contains(event.target)) {
        content.classList.add('is-hidden');
        button.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', closeOnOutside);
      }
    };

    const close = () => {
      content.classList.add('is-hidden');
      content.classList.remove('progress-bar__tooltip-content--flipped');
      button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', closeOnOutside);
    };

    closeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      close();
    });

    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const isHidden = content.classList.toggle('is-hidden');
      button.setAttribute('aria-expanded', String(!isHidden));

      if (isHidden) {
        content.classList.remove('progress-bar__tooltip-content--flipped');
        document.removeEventListener('click', closeOnOutside);
      } else {
        const rect = content.getBoundingClientRect();
        // fits within the viewport
        if (rect.left < 0) {
          content.classList.add('progress-bar__tooltip-content--flipped');
        }
        document.addEventListener('click', closeOnOutside);
      }
    });

    wrapper.appendChild(button);
    content.appendChild(arrow);
    content.appendChild(contentText);
    content.appendChild(closeButton);
    wrapper.appendChild(content);

    return { wrapper, textEl: contentText };
  }

  render() {
    const { currentSpend, estimatedSpend } = this.config;
    const isNegative = currentSpend < 0;
    const showEstimated = !isNegative && estimatedSpend !== null && estimatedSpend !== undefined;

    const currentPercent = this.toPercent(currentSpend);
    const estimatedPercent = showEstimated ? this.toPercent(estimatedSpend) : 0;
    const activeValue = showEstimated ? estimatedSpend : currentSpend;
    const activeLevelKey = this.getLevelKeyForValue(activeValue);

    this._props.currentPercent = currentPercent;
    this._props.estimatedPercent = estimatedPercent;
    this._props.showEstimated = showEstimated;

    this.renderActiveLevel(activeLevelKey);
    this.renderBars(currentPercent, estimatedPercent, showEstimated, isNegative);
    this.renderSpendValues(currentSpend, estimatedSpend, showEstimated);
    this.positionSpend();
  }

  renderActiveLevel(activeLevelKey) {
    const items = this._refs.levels.querySelectorAll('.progress-bar__level');

    items.forEach((item) => {
      item.classList.toggle(
        'progress-bar__level--active',
        item.dataset.levelKey === activeLevelKey,
      );
    });
  }

  renderBars(currentPercent, estimatedPercent, showEstimated, isNegative) {
    const { currentFill, estimatedFill, currentKnob, estimatedKnob } = this._refs;

    if (estimatedFill) estimatedFill.classList.toggle('is-hidden', !showEstimated);
    if (estimatedKnob) estimatedKnob.classList.toggle('is-hidden', !showEstimated);

    if (currentKnob) {
      currentKnob.classList.toggle('progress-bar__knob--negative', Boolean(isNegative));
      currentKnob.style.left = `${currentPercent}%`;
    }
    if (estimatedKnob) estimatedKnob.style.left = `${estimatedPercent}%`;

    // Animate the fills from zero on the next frame.
    if (currentFill) currentFill.style.width = '0%';
    if (estimatedFill) estimatedFill.style.width = '0%';

    window.requestAnimationFrame(() => {
      if (currentFill) currentFill.style.width = `${currentPercent}%`;
      if (estimatedFill) estimatedFill.style.width = showEstimated ? `${estimatedPercent}%` : '0%';
    });
  }

  renderSpendValues(currentSpend, estimatedSpend, showEstimated) {
    const { currentValueEl, estimatedSpendItem, estimatedValueEl } = this._props;

    currentValueEl.textContent = this.formatCurrency(currentSpend);
    estimatedSpendItem.classList.toggle('is-hidden', !showEstimated);

    if (showEstimated) {
      estimatedValueEl.textContent = this.formatCurrency(estimatedSpend);
    }
  }

  positionSpend() {
    const {
      currentSpendItem,
      estimatedSpendItem,
      currentPercent,
      estimatedPercent,
      showEstimated,
    } = this._props;
    const trackWidth = this._refs.track.offsetWidth;

    if (!trackWidth) return;

    // Measure both labels first, then set positions (avoids extra layout recalculations).
    const currentWidth = currentSpendItem.offsetWidth;
    const estimatedWidth = showEstimated ? estimatedSpendItem.offsetWidth : 0;

    const currentLeft = clamp(
      (currentPercent / 100) * trackWidth - currentWidth / 2,
      0,
      Math.max(trackWidth - currentWidth, 0),
    );
    currentSpendItem.style.left = `${currentLeft}px`;

    if (showEstimated) {
      const estimatedLeft = clamp(
        (estimatedPercent / 100) * trackWidth - estimatedWidth / 2,
        0,
        Math.max(trackWidth - estimatedWidth, 0),
      );

      const wouldOverlap = estimatedLeft < currentLeft + currentWidth + SPEND_LABEL_GAP;
      currentSpendItem.classList.toggle('is-hidden', wouldOverlap);
      this.updateTooltipText(wouldOverlap);

      estimatedSpendItem.style.left = `${estimatedLeft}px`;
    }
  }

  updateTooltipText(includeCurrentSpend) {
    const { estimatedTooltipTextEl } = this._props;
    if (!estimatedTooltipTextEl) return;

    const { tooltip, currentSpendLabel, currentSpend } = this.config;

    estimatedTooltipTextEl.textContent = '';

    if (includeCurrentSpend) {
      estimatedTooltipTextEl.appendChild(document.createTextNode(`${currentSpendLabel} `));

      const amount = document.createElement('strong');
      amount.textContent = this.formatCurrency(currentSpend);
      estimatedTooltipTextEl.appendChild(amount);

      estimatedTooltipTextEl.appendChild(document.createTextNode('.'));
      estimatedTooltipTextEl.appendChild(document.createElement('br'));
      estimatedTooltipTextEl.appendChild(document.createTextNode(tooltip));
    } else {
      estimatedTooltipTextEl.textContent = tooltip;
    }
  }
}
